import { Alert, Badge, Button, FieldBlock, Input, Select, Tabs, Textarea, useToast } from '@orbind/ui'
import { useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import {
  AUTOMATION_ACTION_KINDS,
  AUTOMATION_CONDITION_FIELDS,
  AUTOMATION_CONDITION_OPERATORS,
  AUTOMATION_RULES,
  AUTOMATION_TRIGGERS,
  findAutomationRule,
  type AutomationRule,
  type AutomationRunStatus,
} from '../mocks/automations.ts'
import '../features/work-item/wi.css'
import './automations.css'

/** Filtro rapido do explorer. */
type RuleFilter = 'all' | 'active' | 'inactive'

const RULE_FILTERS: readonly { id: RuleFilter; label: string }[] = [
  { id: 'all', label: 'Todas' },
  { id: 'active', label: 'Ativas' },
  { id: 'inactive', label: 'Inativas' },
]

const DATE_TIME = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const SHORT_DATE_TIME = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})

function formatMoment(iso: string): string {
  return DATE_TIME.format(new Date(iso))
}

function formatShortMoment(iso: string): string {
  return SHORT_DATE_TIME.format(new Date(iso))
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`
  return `${(ms / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} s`
}

/** Rotulo, icone e tom de cada resultado de execucao (nunca so cor). */
const RUN_STATUS_META: Record<AutomationRunStatus, { label: string; icon: string; tone: 'success' | 'danger' | 'neutral' }> = {
  success: { label: 'Sucesso', icon: 'fas fa-check-circle', tone: 'success' },
  failed: { label: 'Falhou', icon: 'fas fa-times-circle', tone: 'danger' },
  skipped: { label: 'Ignorada', icon: 'fas fa-minus-circle', tone: 'neutral' },
}

/**
 * AUT-01..03 — Automacoes.
 *
 * Split view no mesmo padrao de Quadros: explorer de regras a esquerda
 * (reusando as classes `wi-*`) e, a direita, a regra selecionada com as abas
 * Configuracao (gatilho -> condicoes -> acoes) e Execucoes.
 *
 * DADOS MOCADOS: nao ha rota de API para automacoes. Ativar/desativar e criar
 * regra so mudam o estado local desta tela.
 */
export function AutomationsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { id: routeId = null } = useParams()
  const toast = useToast()

  const isBuilder = location.pathname.endsWith('/automations/new')
  const isRunsRoute = location.pathname.endsWith('/runs')

  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<RuleFilter>('all')
  // Ativar/desativar e mocado: guardamos so o que o usuario mexeu nesta sessao.
  const [activeOverrides, setActiveOverrides] = useState<Record<string, boolean>>({})

  const isActive = (rule: AutomationRule) => activeOverrides[rule.id] ?? rule.isActive

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    return AUTOMATION_RULES.filter((rule) => {
      if (filter === 'active' && !(activeOverrides[rule.id] ?? rule.isActive)) return false
      if (filter === 'inactive' && (activeOverrides[rule.id] ?? rule.isActive)) return false
      if (term === '') return true
      return (
        rule.name.toLowerCase().includes(term) ||
        rule.description.toLowerCase().includes(term) ||
        rule.trigger.label.toLowerCase().includes(term)
      )
    })
  }, [search, filter, activeOverrides])

  const selected = findAutomationRule(isBuilder ? null : routeId)
  const activeCount = AUTOMATION_RULES.filter((rule) => activeOverrides[rule.id] ?? rule.isActive).length

  return (
    <div className="ob-root wi-split">
      <aside className="wi-explorer" style={{ width: 340 }}>
        <div className="wi-explorer__head">
          <h1 className="wi-explorer__title">Automações</h1>
          <button
            type="button"
            className="wi-explorer__create"
            aria-label="Nova regra de automação"
            title="Nova regra de automação"
            onClick={() => navigate('/automations/new')}
          >
            <i className="fas fa-plus" aria-hidden style={{ fontSize: 12 }} />
          </button>
        </div>

        <div className="wi-explorer__searchwrap">
          <div className="wi-explorer__searchbox">
            <i className="fas fa-search" aria-hidden />
            <input
              className="wi-explorer__search"
              type="search"
              placeholder="Buscar regras…"
              aria-label="Buscar regras de automação"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        <div className="wi-explorer__chips">
          {RULE_FILTERS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`wi-explorer__chip${filter === option.id ? ' wi-explorer__chip--on' : ''}`}
              aria-pressed={filter === option.id}
              onClick={() => setFilter(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="wi-explorer__list">
          {rows.map((rule) => {
            const on = isActive(rule)
            return (
              <button
                key={rule.id}
                type="button"
                className={`wi-row${rule.id === selected?.id ? ' wi-row--on' : ''}`}
                onClick={() => navigate(`/automations/${rule.id}`)}
              >
                <span className={`aut-row__icon${on ? '' : ' aut-row__icon--off'}`}>
                  <i className={rule.trigger.icon} aria-hidden style={{ fontSize: 13 }} />
                </span>
                <span className="wi-row__main">
                  <span className="wi-row__top">
                    <span className="wi-row__title">{rule.name}</span>
                  </span>
                  <span className="wi-row__bottom">
                    <span className="wi-row__code">
                      {rule.lastRunAt
                        ? `Última execução ${formatShortMoment(rule.lastRunAt)}`
                        : 'Nunca executada'}
                      {' · '}
                      {rule.actions.length} {rule.actions.length === 1 ? 'ação' : 'ações'}
                    </span>
                  </span>
                </span>
                <Badge tone={on ? 'success' : 'neutral'} icon={on ? 'fas fa-play' : 'fas fa-pause'}>
                  {on ? 'Ativa' : 'Inativa'}
                </Badge>
              </button>
            )
          })}

          {rows.length === 0 ? (
            <div className="wi-empty" style={{ padding: '44px 24px' }}>
              <div className="wi-empty__icon">
                <i className="fas fa-bolt" aria-hidden />
              </div>
              <span className="wi-empty__title">Nenhuma regra encontrada</span>
              <span className="wi-empty__text">
                Nenhuma automação combina com a busca e o filtro atuais. Limpe os filtros ou crie uma regra nova.
              </span>
              <div className="aut-empty__actions">
                <Button
                  variant="secondary"
                  icon="fas fa-eraser"
                  onClick={() => {
                    setSearch('')
                    setFilter('all')
                  }}
                >
                  Limpar filtros
                </Button>
                <Button variant="primary" icon="fas fa-plus" onClick={() => navigate('/automations/new')}>
                  Nova regra
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="aut-explorer__footer">
          {rows.length} {rows.length === 1 ? 'regra' : 'regras'} · {activeCount} ativas
        </div>
      </aside>

      <div className="aut-content">
        {isBuilder ? (
          <RuleBuilder
            onCancel={() => navigate('/automations')}
            onCreate={(name) => {
              toast.push({
                tone: 'success',
                message: `Regra "${name}" criada apenas nesta tela — as automações ainda não têm API.`,
              })
              navigate('/automations')
            }}
          />
        ) : selected ? (
          <RuleDetail
            rule={selected}
            active={isActive(selected)}
            tab={isRunsRoute ? 'runs' : 'config'}
            onTabChange={(tab) =>
              navigate(tab === 'runs' ? `/automations/${selected.id}/runs` : `/automations/${selected.id}`)
            }
            onToggleActive={() => {
              const next = !isActive(selected)
              setActiveOverrides((current) => ({ ...current, [selected.id]: next }))
              toast.push({
                tone: next ? 'success' : 'info',
                message: next ? 'Regra ativada (apenas nesta tela).' : 'Regra desativada (apenas nesta tela).',
              })
            }}
            onTestRun={() =>
              toast.push({ tone: 'info', message: 'Execução de teste indisponível: as automações ainda não têm API.' })
            }
          />
        ) : (
          <div className="aut-placeholder">
            <div className="wi-empty">
              <div className="wi-empty__icon">
                <i className="fas fa-bolt" aria-hidden />
              </div>
              <span className="wi-empty__title">Selecione uma regra</span>
              <span className="wi-empty__text">
                Escolha uma automação no explorer para ver o gatilho, as condições, as ações e o histórico de execuções.
              </span>
              <div className="aut-empty__actions">
                <Button variant="primary" icon="fas fa-plus" onClick={() => navigate('/automations/new')}>
                  Nova regra
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ------------------------------------------------------------- detalhe ---

function RuleDetail({
  rule,
  active,
  tab,
  onTabChange,
  onToggleActive,
  onTestRun,
}: {
  rule: AutomationRule
  active: boolean
  tab: 'config' | 'runs'
  onTabChange: (tab: 'config' | 'runs') => void
  onToggleActive: () => void
  onTestRun: () => void
}) {
  return (
    <div className="aut-detail">
      <header className="aut-detail__head">
        <div className="aut-detail__ident">
          <div className="aut-detail__titlerow">
            <h2 className="aut-detail__title">{rule.name}</h2>
            <Badge tone={active ? 'success' : 'neutral'} icon={active ? 'fas fa-play' : 'fas fa-pause'}>
              {active ? 'Ativa' : 'Inativa'}
            </Badge>
          </div>
          <p className="aut-detail__desc">{rule.description}</p>
        </div>
        <div className="aut-detail__actions">
          <Button variant="secondary" icon="fas fa-vial" onClick={onTestRun}>
            Executar teste
          </Button>
          <Button
            variant={active ? 'secondary' : 'primary'}
            icon={active ? 'fas fa-pause' : 'fas fa-play'}
            onClick={onToggleActive}
          >
            {active ? 'Desativar' : 'Ativar'}
          </Button>
        </div>
      </header>

      <Tabs
        items={[
          { id: 'config', label: 'Configuração', icon: 'fas fa-sliders-h' },
          { id: 'runs', label: 'Execuções', icon: 'fas fa-history', count: rule.runs.length },
        ]}
        value={tab}
        onChange={(next) => onTabChange(next === 'runs' ? 'runs' : 'config')}
      />

      {tab === 'config' ? <RuleConfig rule={rule} active={active} /> : <RuleRuns rule={rule} onTestRun={onTestRun} />}
    </div>
  )
}

function RuleConfig({ rule, active }: { rule: AutomationRule; active: boolean }) {
  return (
    <div className="aut-config">
      <div className="aut-flow">
        <section className="aut-step" aria-label="Gatilho da regra">
          <div className="aut-step__head">
            <span className="aut-step__kind">Gatilho</span>
            <span className="aut-step__hint">Quando a regra é avaliada</span>
          </div>
          <div className="aut-card">
            <span className="aut-card__icon aut-card__icon--trigger">
              <i className={rule.trigger.icon} aria-hidden />
            </span>
            <div className="aut-card__body">
              <span className="aut-card__title">{rule.trigger.label}</span>
              <span className="aut-card__text">{rule.trigger.description}</span>
            </div>
          </div>
        </section>

        <div className="aut-connector" aria-hidden>
          <i className="fas fa-arrow-down" />
        </div>

        <section className="aut-step" aria-label="Condições da regra">
          <div className="aut-step__head">
            <span className="aut-step__kind">Condições</span>
            <span className="aut-step__hint">
              {rule.conditions.length === 0
                ? 'Nenhuma — a regra roda em toda ocorrência do gatilho'
                : 'Todas precisam ser verdadeiras'}
            </span>
          </div>
          {rule.conditions.length === 0 ? (
            <div className="aut-card aut-card--muted">
              <span className="aut-card__icon aut-card__icon--muted">
                <i className="fas fa-filter" aria-hidden />
              </span>
              <div className="aut-card__body">
                <span className="aut-card__title">Sem condições</span>
                <span className="aut-card__text">
                  Toda ocorrência do gatilho executa as ações. Adicione uma condição para restringir o alcance da regra.
                </span>
              </div>
            </div>
          ) : (
            rule.conditions.map((condition, index) => (
              <div key={condition.id}>
                {index > 0 ? <span className="aut-and">E</span> : null}
                <div className="aut-card">
                  <span className="aut-card__icon aut-card__icon--condition">
                    <i className="fas fa-filter" aria-hidden />
                  </span>
                  <div className="aut-card__body">
                    <span className="aut-card__title">
                      {condition.field} <span className="aut-card__op">{condition.operator}</span>{' '}
                      {condition.value === '—' ? '' : condition.value}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </section>

        <div className="aut-connector" aria-hidden>
          <i className="fas fa-arrow-down" />
        </div>

        <section className="aut-step" aria-label="Ações da regra">
          <div className="aut-step__head">
            <span className="aut-step__kind">Ações</span>
            <span className="aut-step__hint">Executadas na ordem</span>
          </div>
          {rule.actions.map((action, index) => (
            <div key={action.id} className="aut-card">
              <span className="aut-card__icon aut-card__icon--action">
                <i className={action.icon} aria-hidden />
              </span>
              <div className="aut-card__body">
                <span className="aut-card__title">
                  <span className="aut-card__order">{index + 1}.</span> {action.label}
                </span>
                <span className="aut-card__text">{action.detail}</span>
              </div>
            </div>
          ))}
        </section>
      </div>

      <aside className="aut-side" aria-label="Dados da regra">
        {active ? null : (
          <Alert tone="warning" title="Regra inativa">
            Enquanto estiver inativa, a regra não é avaliada nem aparece no histórico de execuções.
          </Alert>
        )}
        <dl className="aut-facts">
          <Fact label="Escopo" value={rule.scope} icon="fas fa-crosshairs" />
          <Fact label="Responsável pela regra" value={rule.ownerName} icon="fas fa-user" />
          <Fact
            label="Execuções nos últimos 30 dias"
            value={`${rule.runCount30d.toLocaleString('pt-BR')}`}
            icon="fas fa-bolt"
          />
          <Fact
            label="Taxa de sucesso"
            value={rule.runCount30d === 0 ? 'Sem execuções no período' : `${rule.successRate}%`}
            icon="fas fa-check-circle"
          />
          <Fact label="Criada em" value={formatMoment(rule.createdAt)} icon="fas fa-calendar-plus" />
          <Fact label="Atualizada em" value={formatMoment(rule.updatedAt)} icon="fas fa-pen" />
        </dl>
      </aside>
    </div>
  )
}

function Fact({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <div className="aut-fact">
      <dt className="aut-fact__label">
        <i className={icon} aria-hidden /> {label}
      </dt>
      <dd className="aut-fact__value">{value}</dd>
    </div>
  )
}

function RuleRuns({ rule, onTestRun }: { rule: AutomationRule; onTestRun: () => void }) {
  if (rule.runs.length === 0) {
    return (
      <div className="aut-runs">
        <div className="wi-empty" style={{ padding: '48px 24px' }}>
          <div className="wi-empty__icon">
            <i className="fas fa-history" aria-hidden />
          </div>
          <span className="wi-empty__title">Nenhuma execução registrada</span>
          <span className="wi-empty__text">
            Esta regra ainda não disparou. Ative-a e aguarde o gatilho, ou registre uma execução de teste para conferir o
            resultado antes de liberar para todo mundo.
          </span>
          <div className="aut-empty__actions">
            <Button variant="primary" icon="fas fa-vial" onClick={onTestRun}>
              Executar teste
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="aut-runs">
      <p className="aut-runs__caption">
        {rule.runs.length} {rule.runs.length === 1 ? 'execução registrada' : 'execuções registradas'} · a mais recente
        primeiro
      </p>
      <ul className="aut-runlist">
        {rule.runs.map((run) => {
          const meta = RUN_STATUS_META[run.status]
          return (
            <li key={run.id} className="aut-run">
              <Badge tone={meta.tone} icon={meta.icon}>
                {meta.label}
              </Badge>
              <div className="aut-run__body">
                <span className="aut-run__target">
                  <span className="aut-run__code">{run.target.code}</span> {run.target.title}
                </span>
                <span className="aut-run__message">{run.message}</span>
              </div>
              <div className="aut-run__meta">
                <span>{formatMoment(run.startedAt)}</span>
                <span className="aut-run__duration">{formatDuration(run.durationMs)}</span>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// ------------------------------------------------------- construtor AUT-02 ---

/** Construtor de regra (mocado): valida o preenchimento e devolve o nome. */
function RuleBuilder({ onCancel, onCreate }: { onCancel: () => void; onCreate: (name: string) => void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [triggerCode, setTriggerCode] = useState<string | null>(null)
  const [conditionField, setConditionField] = useState<string | null>(null)
  const [conditionOperator, setConditionOperator] = useState<string | null>(null)
  const [conditionValue, setConditionValue] = useState('')
  const [actionCode, setActionCode] = useState<string | null>(null)
  const [touched, setTouched] = useState(false)

  const trigger = AUTOMATION_TRIGGERS.find((option) => option.code === triggerCode) ?? null
  const action = AUTOMATION_ACTION_KINDS.find((option) => option.code === actionCode) ?? null
  const nameError = touched && name.trim() === '' ? 'Dê um nome que descreva o efeito da regra.' : null
  const triggerError = touched && trigger === null ? 'Escolha o momento em que a regra é avaliada.' : null
  const actionError = touched && action === null ? 'Escolha pelo menos uma ação.' : null
  const canSubmit = name.trim() !== '' && trigger !== null && action !== null

  return (
    <div className="aut-detail">
      <header className="aut-detail__head">
        <div className="aut-detail__ident">
          <div className="aut-detail__titlerow">
            <h2 className="aut-detail__title">Nova regra de automação</h2>
          </div>
          <p className="aut-detail__desc">
            Monte a regra na ordem em que ela roda: gatilho, condições e ações. A condição é opcional — sem ela, toda
            ocorrência do gatilho executa as ações.
          </p>
        </div>
        <div className="aut-detail__actions">
          <Button variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            icon="fas fa-check"
            onClick={() => {
              setTouched(true)
              if (canSubmit) onCreate(name.trim())
            }}
          >
            Criar regra
          </Button>
        </div>
      </header>

      <div className="aut-builder">
        <section className="aut-builder__block" aria-label="Identificação da regra">
          <h3 className="aut-builder__legend">1. Identificação</h3>
          <FieldBlock label="Nome da regra" required error={nameError}>
            {({ id, invalid }) => (
              <Input
                id={id}
                invalid={invalid}
                value={name}
                placeholder="Ex.: Avisar o responsável quando o item é concluído"
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </FieldBlock>
          <FieldBlock label="Descrição" hint="Explique para o time o que a regra resolve.">
            {({ id }) => (
              <Textarea
                id={id}
                rows={3}
                value={description}
                placeholder="Para que serve esta automação"
                onChange={(event) => setDescription(event.target.value)}
              />
            )}
          </FieldBlock>
        </section>

        <section className="aut-builder__block" aria-label="Gatilho da regra">
          <h3 className="aut-builder__legend">2. Gatilho</h3>
          <FieldBlock label="Quando a regra é avaliada" required error={triggerError}>
            {({ id, invalid }) => (
              <Select
                id={id}
                invalid={invalid}
                value={triggerCode}
                placeholder="Escolha o gatilho"
                options={AUTOMATION_TRIGGERS.map((option) => ({
                  value: option.code,
                  label: option.label,
                  icon: option.icon,
                }))}
                onChange={setTriggerCode}
              />
            )}
          </FieldBlock>
          {trigger ? <p className="aut-builder__note">{trigger.description}</p> : null}
        </section>

        <section className="aut-builder__block" aria-label="Condição da regra">
          <h3 className="aut-builder__legend">3. Condição (opcional)</h3>
          <div className="aut-builder__grid">
            <FieldBlock label="Campo">
              {({ id }) => (
                <Select
                  id={id}
                  value={conditionField}
                  placeholder="Escolha o campo"
                  options={AUTOMATION_CONDITION_FIELDS.map((field) => ({ value: field, label: field }))}
                  onChange={setConditionField}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Operador">
              {({ id }) => (
                <Select
                  id={id}
                  value={conditionOperator}
                  placeholder="Escolha o operador"
                  options={AUTOMATION_CONDITION_OPERATORS.map((operator) => ({ value: operator, label: operator }))}
                  onChange={setConditionOperator}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Valor" hint="Deixe vazio para operadores como “está preenchido”.">
              {({ id }) => (
                <Input
                  id={id}
                  value={conditionValue}
                  placeholder="Ex.: Concluído"
                  onChange={(event) => setConditionValue(event.target.value)}
                />
              )}
            </FieldBlock>
          </div>
        </section>

        <section className="aut-builder__block" aria-label="Ação da regra">
          <h3 className="aut-builder__legend">4. Ação</h3>
          <FieldBlock label="O que acontece quando a regra dispara" required error={actionError}>
            {({ id, invalid }) => (
              <Select
                id={id}
                invalid={invalid}
                value={actionCode}
                placeholder="Escolha a ação"
                options={AUTOMATION_ACTION_KINDS.map((option) => ({
                  value: option.code,
                  label: option.label,
                  icon: option.icon,
                }))}
                onChange={setActionCode}
              />
            )}
          </FieldBlock>
        </section>

        <Alert tone="info" title="Prévia da regra">
          {trigger && action
            ? `${trigger.label}${
                conditionField && conditionOperator
                  ? `, se ${conditionField} ${conditionOperator}${conditionValue.trim() ? ` ${conditionValue.trim()}` : ''}`
                  : ''
              } → ${action.label}.`
            : 'Escolha o gatilho e a ação para ver a frase completa da regra.'}
        </Alert>
      </div>
    </div>
  )
}
